import { Injectable, signal, Signal } from '@angular/core';

export type Theme = 'light' | 'dark';
export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  private readonly _theme = signal<Theme>(this.resolveInitialTheme());
  private readonly _mode = signal<ThemeMode>(
    (localStorage.getItem('theme') as Theme | null) ?? 'system'
  );

  readonly theme: Signal<Theme> = this._theme.asReadonly();
  readonly mode: Signal<ThemeMode> = this._mode.asReadonly();

  constructor() {
    this.applyTheme(this._theme());

    this.mediaQuery.addEventListener('change', (e) => {
      if (this._mode() === 'system') {
        const osTheme: Theme = e.matches ? 'dark' : 'light';
        this._theme.set(osTheme);
        this.applyTheme(osTheme);
      }
    });

    window.addEventListener('beforeprint', () => {
      document.documentElement.classList.remove('dark');
    });
    window.addEventListener('afterprint', () => {
      this.applyTheme(this._theme());
    });
  }

  cycleMode(): void {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(this._mode()) + 1) % order.length];
    this.setMode(next);
  }

  private setMode(mode: ThemeMode): void {
    this._mode.set(mode);

    if (mode === 'system') {
      localStorage.removeItem('theme');
      const osTheme: Theme = this.mediaQuery.matches ? 'dark' : 'light';
      this._theme.set(osTheme);
      this.applyTheme(osTheme);
    } else {
      localStorage.setItem('theme', mode);
      this._theme.set(mode);
      this.applyTheme(mode);
    }
  }

  private resolveInitialTheme(): Theme {
    return (
      (localStorage.getItem('theme') as Theme | null) ??
      (this.mediaQuery.matches ? 'dark' : 'light')
    );
  }

  private applyTheme(theme: Theme): void {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
}
